import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Plus, X, Check, Clock, Calendar as CalendarIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";
import api, { formatApiError } from "@/lib/api";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];
const PAYMENT_MODES = ["Monthly", "Quarterly", "Semestral", "Annual"];

const EMPTY_GUARDIAN = {
  name: "", phone: "", email: "", fb_messenger: "", photo: "", enable_login: false,
};

function todayISO() { return new Date().toISOString().slice(0, 10); }
function ageFromBirthday(birthday) {
  if (!birthday) return "";
  const b = new Date(birthday);
  if (Number.isNaN(b.getTime())) return "";
  const t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) age--;
  return age >= 0 ? age : "";
}

export default function StudentFormDialog({
  open, onClose, schoolId, branchId, schoolName, classes, sections, employees = [], currency, editing, onSaved,
}) {
  const fileInputRef = useRef(null);

  const initial = editing || {};
  const [photo, setPhoto] = useState(initial.photo || "");
  const [status, setStatus] = useState(initial.status || "active");
  const [name, setName] = useState(initial.name || "");
  const [middleInitial, setMiddleInitial] = useState(initial.middle_initial || "");
  const [lastName, setLastName] = useState(initial.last_name || "");
  const [birthday, setBirthday] = useState(initial.birthday || "");
  const [address, setAddress] = useState(initial.address || "");
  const [city, setCity] = useState(initial.city || "");
  const [province, setProvince] = useState(initial.province || "");
  const [country, setCountry] = useState(initial.country || "Philippines");
  const [generalNotes, setGeneralNotes] = useState(initial.general_notes || "");

  const [enrollInClass, setEnrollInClass] = useState(
    editing ? !!initial.enroll_in_class : true
  );
  const [enrollInIndividual, setEnrollInIndividual] = useState(
    editing ? !!initial.enroll_in_individual : false
  );
  const [classId, setClassId] = useState(initial.class_id || "");
  const [sectionId, setSectionId] = useState(initial.section_id || "");
  const [individualClassId, setIndividualClassId] = useState(initial.individual_class_id || "");
  const [individualSchedule, setIndividualSchedule] = useState(
    Array.isArray(initial.individual_schedule) && initial.individual_schedule.length
      ? initial.individual_schedule.map((sl) => ({
          kind: sl.kind || "day",
          day: sl.day || "",
          date: sl.date || "",
          time_start: sl.time_start || "",
          time_end: sl.time_end || "",
          teacher_employee_id: sl.teacher_employee_id || "",
        }))
      : []
  );

  const [tuitionFee, setTuitionFee] = useState(initial.tuition_fee != null ? String(initial.tuition_fee) : "0");
  const [individualClassFee, setIndividualClassFee] = useState(initial.individual_class_fee != null ? String(initial.individual_class_fee) : "0");
  const [modeOfPayment, setModeOfPayment] = useState(initial.mode_of_payment || "Monthly");
  const [tuitionDiscount, setTuitionDiscount] = useState(initial.tuition_discount != null ? String(initial.tuition_discount) : "0");
  const [reservationFee, setReservationFee] = useState(initial.reservation_fee != null ? String(initial.reservation_fee) : "0");
  const [registrationFee, setRegistrationFee] = useState(initial.registration_fee != null ? String(initial.registration_fee) : "0");
  const [requiredInitialPayment, setRequiredInitialPayment] = useState(
    initial.required_initial_payment != null ? String(initial.required_initial_payment) : "0"
  );

  const [guardians, setGuardians] = useState(
    initial.guardians?.length ? initial.guardians.map((g) => ({ ...EMPTY_GUARDIAN, ...g })) : [{ ...EMPTY_GUARDIAN }]
  );

  const [saving, setSaving] = useState(false);

  // Regular classes only for the Class/Section enrollment
  const regularClasses = useMemo(() => classes.filter((c) => c.class_type === "regular" || c.class_type === "modular"), [classes]);
  const individualClasses = useMemo(() => classes.filter((c) => c.class_type === "individual"), [classes]);
  const sectionsForClass = useMemo(
    () => sections.filter((s) => s.class_id === classId),
    [sections, classId]
  );

  // Auto-fill tuition fee from selected section
  useEffect(() => {
    if (!sectionId) return;
    const sec = sections.find((s) => s.id === sectionId);
    if (sec && Number(tuitionFee || 0) === 0) {
      setTuitionFee(String(sec.tuition_fee ?? 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId]);

  // Force-sync tuition when user picks a different section (always overwrite even if user typed)
  const handleSectionChange = (e) => {
    const sid = e.target.value;
    setSectionId(sid);
    const sec = sections.find((s) => s.id === sid);
    if (sec) setTuitionFee(String(sec.tuition_fee ?? 0));
  };

  const handleClassChange = (e) => {
    setClassId(e.target.value);
    setSectionId("");
  };

  // Auto-fill fee from the selected Individual class.
  // When BOTH regular and individual enrollments are active, the fee is
  // stored on its own field so the Regular-class Tuition Fee stays intact.
  // When only Individual enrollment is active, we keep the legacy single-fee
  // behaviour (populates `tuition_fee`) for backward compatibility.
  const handleIndividualClassChange = (e) => {
    const id = e.target.value;
    setIndividualClassId(id);
    const ic = individualClasses.find((c) => c.id === id);
    if (ic && Number(ic.fee || 0) > 0) {
      if (enrollInClass) {
        setIndividualClassFee(String(ic.fee));
      } else {
        setTuitionFee(String(ic.fee));
      }
    }
  };

  const age = useMemo(() => ageFromBirthday(birthday), [birthday]);

  // Derived totals
  const netTuition = useMemo(() => {
    const t = Number(tuitionFee || 0);
    const d = Number(tuitionDiscount || 0);
    return Math.max(0, t - d);
  }, [tuitionFee, tuitionDiscount]);

  const totalInitialFees = useMemo(() => {
    return Number(reservationFee || 0) + Number(requiredInitialPayment || 0);
  }, [reservationFee, requiredInitialPayment]);

  const fmt = (v) => `${currency} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const onPhotoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await compressImage(f, { maxKB: 400, maxDim: 800 });
      setPhoto(url);
    } catch (err) {
      toast.error(err.message || "Failed to process image");
    }
  };

  const onGuardianPhotoChange = async (idx, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await compressImage(f, { maxKB: 400, maxDim: 800 });
      setGuardians((arr) => arr.map((g, i) => i === idx ? { ...g, photo: url } : g));
    } catch (err) { toast.error(err.message || "Failed to process image"); }
  };

  const updateGuardian = (idx, patch) => {
    setGuardians((arr) => arr.map((g, i) => i === idx ? { ...g, ...patch } : g));
  };
  const addGuardian = () => setGuardians((arr) => [...arr, { ...EMPTY_GUARDIAN }]);
  const removeGuardian = (idx) => setGuardians((arr) => arr.length === 1 ? arr : arr.filter((_, i) => i !== idx));

  const validate = () => {
    if (!branchId) { toast.error("Select a branch first"); return false; }
    if (!name.trim()) { toast.error("First Name is required"); return false; }
    // All other personal-info + guardian fields are optional during enrollment.
    // The student record is saved as "Profile incomplete" and the admin can
    // finish it later. We do still validate the chosen enrollment options:
    if (enrollInClass && (!classId || !sectionId)) { toast.error("Pick a class and section for Class enrollment"); return false; }
    if (enrollInIndividual && !individualClassId) { toast.error("Pick an Individual Session class"); return false; }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {
      school_id: schoolId,
      branch_id: branchId,
      photo,
      name: name.trim(),
      middle_initial: middleInitial.trim(),
      last_name: lastName.trim(),
      birthday,
      address: address.trim(),
      city: city.trim(),
      province: province.trim(),
      country: country.trim(),
      general_notes: generalNotes.trim(),
      status,
      enroll_in_class: enrollInClass,
      class_id: enrollInClass ? classId : null,
      section_id: enrollInClass ? sectionId : null,
      enroll_in_individual: enrollInIndividual,
      individual_class_id: enrollInIndividual ? individualClassId : null,
      individual_schedule: enrollInIndividual ? individualSchedule
        .filter((sl) => (sl.kind === "day" ? !!sl.day : !!sl.date))
        .map((sl) => ({
          kind: sl.kind,
          day: sl.kind === "day" ? sl.day : null,
          date: sl.kind === "date" ? sl.date : null,
          time_start: sl.time_start || null,
          time_end: sl.time_end || null,
          teacher_employee_id: sl.teacher_employee_id || null,
        })) : [],
      tuition_fee: Number(tuitionFee || 0),
      individual_class_fee: Number(individualClassFee || 0),
      mode_of_payment: modeOfPayment,
      tuition_discount: Number(tuitionDiscount || 0),
      reservation_fee: Number(reservationFee || 0),
      registration_fee: Number(registrationFee || 0),
      required_initial_payment: Number(requiredInitialPayment || 0),
      guardians: guardians
        .filter((g) => g.name && g.phone)
        .map((g) => ({
          name: g.name.trim(),
          phone: g.phone.trim(),
          email: (g.email || "").trim(),
          fb_messenger: (g.fb_messenger || "").trim(),
          photo: g.photo || "",
          enable_login: !!g.enable_login,
        })),
    };
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/students/${editing.id}`, payload);
        toast.success("Student updated");
      } else {
        await api.post("/students", payload);
        toast.success("Student added");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-4xl rounded-3xl max-h-[92vh] overflow-hidden p-0 flex flex-col" data-testid="student-dialog">
        <DialogHeader className="px-6 sm:px-8 pt-6 pb-3 border-b border-brand-purple/10">
          <DialogTitle className="font-heading text-2xl sm:text-3xl tracking-tight">{editing ? "Edit Student" : "Add Student"}</DialogTitle>
          <DialogDescription className="text-brand-inkMuted">{schoolName ? `For ${schoolName}` : ""}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 sm:px-8 py-5 space-y-5" data-testid="student-form">
          {/* Student Information */}
          <SectionCard title="Student Information">
            <div className="flex items-center gap-4 mb-4">
              <PhotoCircle src={photo} />
              <div>
                <Button
                  type="button" variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-2xl h-10"
                  data-testid="student-photo-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-1.5" /> Upload Photo
                </Button>
                <p className="text-[11px] text-brand-inkMuted mt-1">JPEG, JPG, PNG · max 500 KB</p>
                <input
                  ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/jpg"
                  className="hidden" onChange={onPhotoChange} data-testid="student-photo-input"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <Field label="Branch" required>
                <Input value={schoolName ? `${schoolName} — current branch` : "Current branch"} readOnly className="rounded-2xl h-11 bg-[#F6F8FE] cursor-not-allowed" />
              </Field>
              <Field label="Status">
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="ovw-select" data-testid="student-status-select">
                  {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mb-3">
              <Field label="Name" required>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-2xl h-11" data-testid="student-name-input" />
              </Field>
              <Field label="M.I.">
                <Input value={middleInitial} maxLength={3} onChange={(e) => setMiddleInitial(e.target.value.toUpperCase())} className="rounded-2xl h-11" data-testid="student-mi-input" />
              </Field>
              <Field label="Last Name">
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} className="rounded-2xl h-11" data-testid="student-last-name-input" />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <Field label="Birthday">
                <Input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} max={todayISO()} className="rounded-2xl h-11" data-testid="student-birthday-input" />
              </Field>
              <Field label="Age">
                <Input value={age === "" ? "—" : `${age}`} readOnly className="rounded-2xl h-11 bg-[#F6F8FE] tabular-nums" data-testid="student-age-input" />
              </Field>
            </div>

            <div className="mb-3">
              <Field label="Address">
                <Input value={address} onChange={(e) => setAddress(e.target.value)} className="rounded-2xl h-11" data-testid="student-address-input" />
              </Field>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mb-3">
              <Field label="City / Town">
                <Input value={city} onChange={(e) => setCity(e.target.value)} className="rounded-2xl h-11" data-testid="student-city-input" />
              </Field>
              <Field label="Province">
                <Input value={province} onChange={(e) => setProvince(e.target.value)} className="rounded-2xl h-11" data-testid="student-province-input" />
              </Field>
              <Field label="Country">
                <Input value={country} onChange={(e) => setCountry(e.target.value)} className="rounded-2xl h-11" data-testid="student-country-input" />
              </Field>
            </div>

            <Field label="General Notes">
              <Textarea value={generalNotes} onChange={(e) => setGeneralNotes(e.target.value)} className="rounded-2xl min-h-[80px]" data-testid="student-notes-input" />
            </Field>
          </SectionCard>

          {/* Enrollment Details */}
          <SectionCard title="Enrollment Details">
            <p className="text-sm text-brand-inkMuted mb-3">Select at least one. A student can be enrolled in a class, an individual session, or both.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <EnrollToggle
                checked={enrollInClass} onChange={setEnrollInClass}
                title="Enroll in Class / Section" subtitle="Standard class with tuition fee."
                testid="enroll-class-toggle"
              />
              <EnrollToggle
                checked={enrollInIndividual} onChange={setEnrollInIndividual}
                title="Enroll in Individual Session" subtitle="Tutorials, therapy, one-on-one programs."
                testid="enroll-individual-toggle"
              />
            </div>

            {enrollInClass && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3 p-3 rounded-2xl bg-[#F6F8FE] border border-brand-purple/10" data-testid="enroll-class-fields">
                <Field label="Class / Level" required>
                  <select value={classId} onChange={handleClassChange} className="ovw-select" data-testid="student-class-select">
                    <option value="">Select class</option>
                    {regularClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </Field>
                <Field label="Section" required>
                  <select value={sectionId} onChange={handleSectionChange} className="ovw-select" data-testid="student-section-select" disabled={!classId}>
                    <option value="">{classId ? "Select section" : "Choose a class first"}</option>
                    {sectionsForClass.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
              </div>
            )}

            {enrollInIndividual && (
              <div className="mt-4 p-3 rounded-2xl bg-[#F6F8FE] border border-brand-purple/10 space-y-4" data-testid="enroll-individual-fields">
                <Field label="Individual Session Class" required>
                  <select value={individualClassId} onChange={handleIndividualClassChange} className="ovw-select" data-testid="student-individual-class-select">
                    <option value="">Select individual class</option>
                    {individualClasses.map((c) => <option key={c.id} value={c.id}>{c.name}{c.fee ? ` — ${currency} ${Number(c.fee).toLocaleString()}` : ""}</option>)}
                  </select>
                </Field>

                <IndividualScheduleBuilder
                  slots={individualSchedule}
                  setSlots={setIndividualSchedule}
                  employees={employees}
                />
              </div>
            )}
          </SectionCard>

          {/* Tuition & Fees */}
          <SectionCard title={enrollInIndividual && !enrollInClass ? "Fee" : "Tuition and Fees"}>
            {enrollInIndividual && !enrollInClass ? (
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Fee" required>
                  <CurrencyInput value={tuitionFee} onChange={setTuitionFee} currency={currency} testid="student-tuition-input" className="bg-[#F6F8FE]" />
                  <p className="text-[11px] text-brand-inkMuted mt-1">Auto-filled from the selected Individual Session class fee.</p>
                </Field>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Tuition Fee" required>
                    <CurrencyInput value={tuitionFee} onChange={setTuitionFee} currency={currency} testid="student-tuition-input" className="bg-[#F6F8FE]" />
                    <p className="text-[11px] text-brand-inkMuted mt-1">Auto-filled from selected section. To edit, update the section tuition fee.</p>
                  </Field>
                  {enrollInIndividual && (
                    <Field label="Individual Class Fee">
                      <CurrencyInput value={individualClassFee} onChange={setIndividualClassFee} currency={currency} testid="student-individual-fee-input" className="bg-[#F6F8FE]" />
                      <p className="text-[11px] text-brand-inkMuted mt-1">Auto-filled from the selected Individual class. Kept separate so multi-class enrollments retain both fees.</p>
                    </Field>
                  )}
                  <Field label="Mode of Payment" required>
                    <select value={modeOfPayment} onChange={(e) => setModeOfPayment(e.target.value)} className="ovw-select" data-testid="student-payment-mode-select">
                      {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </Field>
                  <Field label="Tuition Discount">
                    <CurrencyInput value={tuitionDiscount} onChange={setTuitionDiscount} currency={currency} testid="student-discount-input" />
                  </Field>
                  <Field label="Net Tuition Fee">
                    <Input
                      value={fmt(netTuition)} readOnly
                      className="rounded-2xl h-11 bg-[#F6F8FE] tabular-nums font-heading font-semibold"
                      data-testid="student-net-tuition"
                    />
                  </Field>
                  <Field label="Reservation Fee">
                    <CurrencyInput value={reservationFee} onChange={setReservationFee} currency={currency} testid="student-reservation-input" />
                  </Field>
                  <Field label="Registration Fee">
                    <CurrencyInput value={registrationFee} onChange={setRegistrationFee} currency={currency} testid="student-registration-input" />
                  </Field>
                  <Field label="Required Initial Payment" className="sm:col-span-2">
                    <CurrencyInput value={requiredInitialPayment} onChange={setRequiredInitialPayment} currency={currency} testid="student-initial-input" />
                  </Field>
                </div>
                <div className="mt-3 p-3 rounded-2xl bg-[#EEF3FF] border border-brand-purple/15 text-sm text-foreground" data-testid="student-total-initial">
                  <span className="font-heading">Total Initial Fees:</span>{" "}
                  <span className="font-heading font-bold tabular-nums">{fmt(totalInitialFees)}</span>
                  <span className="text-brand-inkMuted ml-2">(Registration Fee is separate)</span>
                </div>
              </>
            )}
          </SectionCard>

          {/* Guardians */}
          <SectionCard title="Parent / Guardian Information">
            <div className="space-y-3">
              {guardians.map((g, idx) => (
                <GuardianCard
                  key={idx} idx={idx} guardian={g}
                  onChange={(patch) => updateGuardian(idx, patch)}
                  onPhotoChange={(e) => onGuardianPhotoChange(idx, e)}
                  onRemove={guardians.length > 1 ? () => removeGuardian(idx) : null}
                />
              ))}
            </div>
            <Button type="button" variant="outline" onClick={addGuardian} className="rounded-2xl h-10 mt-3" data-testid="add-guardian-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add Guardian
            </Button>
          </SectionCard>
        </form>

        <DialogFooter className="px-6 sm:px-8 py-4 border-t border-brand-purple/10 bg-[#FAFBFE] flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11" data-testid="student-cancel-btn">Cancel</Button>
          <Button type="button" onClick={submit} disabled={saving} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-6" data-testid="student-submit-btn">
            {saving ? "Saving..." : (editing ? "Save" : "Add Student")}
          </Button>
        </DialogFooter>

        <style>{`
          .ovw-select {
            width: 100%; height: 2.75rem; border-radius: 1rem;
            border: 1px solid rgba(37, 99, 235, 0.18); background: #fff;
            padding: 0 0.75rem; font-size: 0.875rem; color: hsl(var(--foreground));
          }
          .ovw-select:focus-visible { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring)); }
          .ovw-select:disabled { background: #F1F4FB; cursor: not-allowed; }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------- Subparts -------------------------------- */
function SectionCard({ title, children }) {
  return (
    <section className="rounded-2xl border border-brand-purple/10 bg-white p-4 sm:p-5">
      <p className="font-heading font-semibold text-foreground text-sm tracking-wider uppercase mb-3 pb-2 border-b border-brand-purple/10">{title}</p>
      {children}
    </section>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="font-heading text-sm text-foreground">
        {label}{required && <span className="text-[#C62828] ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function CurrencyInput({ value, onChange, currency, testid, className = "" }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkMuted">{currency}</span>
      <Input
        type="number" min="0" step="0.01" value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`rounded-2xl h-11 pl-8 tabular-nums ${className}`}
        data-testid={testid}
      />
    </div>
  );
}

function PhotoCircle({ src }) {
  if (src) {
    return <img src={src} alt="" className="w-16 h-16 rounded-full object-cover border border-brand-purple/15" />;
  }
  return (
    <div className="w-16 h-16 rounded-full bg-[#EEF3FF] flex items-center justify-center text-brand-inkMuted text-xl font-heading">?</div>
  );
}

function EnrollToggle({ checked, onChange, title, subtitle, testid }) {
  return (
    <button
      type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className={`text-left rounded-2xl border px-4 py-3 transition flex items-start gap-3 ${
        checked ? "border-brand-purple bg-brand-purpleLight/40 shadow-sm" : "border-input hover:bg-brand-purpleLight/20"
      }`}
    >
      <span className={`w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center shrink-0 ${
        checked ? "border-brand-purple bg-brand-purple text-white" : "border-input bg-white"
      }`}>
        {checked && <Check className="w-3 h-3" strokeWidth={3} />}
      </span>
      <div>
        <p className="font-heading font-semibold text-foreground text-sm">{title}</p>
        <p className="text-[12px] text-brand-inkMuted mt-0.5">{subtitle}</p>
      </div>
    </button>
  );
}

function GuardianCard({ idx, guardian, onChange, onPhotoChange, onRemove }) {
  const ref = useRef(null);
  return (
    <div className="rounded-2xl border border-brand-purple/10 bg-[#FAFBFE] p-4" data-testid={`guardian-card-${idx}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="font-heading font-semibold text-foreground">Guardian {idx + 1}</p>
        {onRemove && (
          <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="h-8 w-8 text-brand-inkMuted hover:text-[#C62828]" data-testid={`guardian-remove-${idx}`}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
      <div className="flex items-center gap-4 mb-3">
        <PhotoCircle src={guardian.photo} />
        <div>
          <Button type="button" variant="outline" onClick={() => ref.current?.click()} className="rounded-2xl h-10" data-testid={`guardian-photo-btn-${idx}`}>
            <Upload className="w-4 h-4 mr-1.5" /> Upload Photo
          </Button>
          <p className="text-[11px] text-brand-inkMuted mt-1">JPEG, JPG, PNG · max 500 KB</p>
          <input ref={ref} type="file" accept="image/jpeg,image/png,image/jpg" className="hidden" onChange={onPhotoChange} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Guardian Full Name">
          <Input value={guardian.name} onChange={(e) => onChange({ name: e.target.value })} className="rounded-2xl h-11" data-testid={`guardian-name-${idx}`} />
        </Field>
        <Field label="Guardian Phone Number">
          <Input value={guardian.phone} onChange={(e) => onChange({ phone: e.target.value })} className="rounded-2xl h-11" data-testid={`guardian-phone-${idx}`} />
        </Field>
        <Field label="Guardian Email Address">
          <Input type="email" value={guardian.email} onChange={(e) => onChange({ email: e.target.value })} placeholder="example@email.com" className="rounded-2xl h-11" data-testid={`guardian-email-${idx}`} />
        </Field>
        <Field label="Guardian FB Messenger">
          <Input value={guardian.fb_messenger} onChange={(e) => onChange({ fb_messenger: e.target.value })} placeholder="Name, link or handle" className="rounded-2xl h-11" data-testid={`guardian-fb-${idx}`} />
        </Field>
      </div>
      <label className="flex items-center gap-2 mt-3 cursor-pointer" data-testid={`guardian-login-toggle-${idx}`}>
        <Checkbox checked={!!guardian.enable_login} onCheckedChange={(v) => onChange({ enable_login: !!v })} />
        <span className="text-sm text-foreground">Enable Parent Login Access</span>
      </label>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Individual Session Schedule Builder                                        */
/* -------------------------------------------------------------------------- */
const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function IndividualScheduleBuilder({ slots, setSlots, employees }) {
  const usedDays = slots.filter((s) => s.kind === "day" && s.day).map((s) => s.day);
  const dayKindCount = slots.filter((s) => s.kind === "day").length;
  const canAddDay = dayKindCount < 7;

  const update = (idx, patch) => {
    setSlots((arr) => arr.map((s, i) => {
      if (i !== idx) return s;
      const next = { ...s, ...patch };
      // If switching kind, clear the other field to avoid stale state
      if (patch.kind === "day") next.date = "";
      if (patch.kind === "date") next.day = "";
      return next;
    }));
  };
  const add = (kind) => {
    if (kind === "day" && !canAddDay) return;
    setSlots((arr) => [...arr, {
      kind, day: "", date: "", time_start: "", time_end: "", teacher_employee_id: "",
    }]);
  };
  const remove = (idx) => setSlots((arr) => arr.filter((_, i) => i !== idx));

  return (
    <div data-testid="individual-schedule-builder">
      <div className="flex items-center justify-between mb-2">
        <Label className="font-heading flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-brand-purple" /> Schedule
        </Label>
        <div className="flex gap-2">
          <Button
            type="button" variant="outline"
            onClick={() => add("day")}
            disabled={!canAddDay}
            className="rounded-xl h-9 px-3 text-sm" data-testid="add-day-slot-btn"
            title={canAddDay ? "Add a weekly day slot" : "All 7 weekdays are already added"}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Day of Week
          </Button>
          <Button
            type="button" variant="outline"
            onClick={() => add("date")}
            className="rounded-xl h-9 px-3 text-sm" data-testid="add-date-slot-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Specific Date
          </Button>
        </div>
      </div>

      {slots.length === 0 ? (
        <div className="px-3 py-4 rounded-2xl border border-dashed border-brand-purple/20 text-sm text-brand-inkMuted italic" data-testid="schedule-empty">
          No schedule yet — add a weekly day or a specific date.
        </div>
      ) : (
        <div className="space-y-2">
          {slots.map((sl, idx) => (
            <div
              key={idx}
              className="rounded-2xl bg-white border border-brand-purple/10 px-3 py-2.5"
              data-testid={`ind-slot-${idx}`}
            >
              <div className="grid grid-cols-[110px_minmax(0,1fr)_110px_110px_minmax(0,1.2fr)_40px] gap-2 items-end">
                {/* Kind toggle */}
                <div>
                  {idx === 0 && <Label className="text-[11px] text-brand-inkMuted">Type</Label>}
                  <select
                    value={sl.kind}
                    onChange={(e) => update(idx, { kind: e.target.value })}
                    className="w-full h-10 rounded-xl border border-input bg-white px-2 text-sm mt-1"
                    data-testid={`ind-slot-kind-${idx}`}
                  >
                    <option value="day">Day of week</option>
                    <option value="date">Specific date</option>
                  </select>
                </div>

                {/* Day or Date */}
                <div>
                  {idx === 0 && <Label className="text-[11px] text-brand-inkMuted">{sl.kind === "day" ? "Day" : "Date"}</Label>}
                  {sl.kind === "day" ? (
                    <select
                      value={sl.day || ""}
                      onChange={(e) => update(idx, { day: e.target.value })}
                      className="w-full h-10 rounded-xl border border-input bg-white px-2 text-sm mt-1"
                      data-testid={`ind-slot-day-${idx}`}
                    >
                      <option value="">Select day</option>
                      {DAYS_OF_WEEK.map((d) => (
                        <option
                          key={d} value={d}
                          disabled={usedDays.includes(d) && d !== sl.day}
                        >
                          {d}{usedDays.includes(d) && d !== sl.day ? " — taken" : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="relative mt-1">
                      <CalendarIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-inkMuted pointer-events-none" />
                      <Input
                        type="date"
                        value={sl.date || ""}
                        onChange={(e) => update(idx, { date: e.target.value })}
                        className="rounded-xl h-10 pl-8"
                        data-testid={`ind-slot-date-${idx}`}
                      />
                    </div>
                  )}
                </div>

                {/* Time start */}
                <div>
                  {idx === 0 && <Label className="text-[11px] text-brand-inkMuted">Start</Label>}
                  <Input
                    type="time"
                    value={sl.time_start || ""}
                    onChange={(e) => update(idx, { time_start: e.target.value })}
                    className="rounded-xl h-10 mt-1"
                    data-testid={`ind-slot-start-${idx}`}
                  />
                </div>
                {/* Time end */}
                <div>
                  {idx === 0 && <Label className="text-[11px] text-brand-inkMuted">End</Label>}
                  <Input
                    type="time"
                    value={sl.time_end || ""}
                    onChange={(e) => update(idx, { time_end: e.target.value })}
                    className="rounded-xl h-10 mt-1"
                    data-testid={`ind-slot-end-${idx}`}
                  />
                </div>
                {/* Teacher */}
                <div>
                  {idx === 0 && <Label className="text-[11px] text-brand-inkMuted">Teacher</Label>}
                  <select
                    value={sl.teacher_employee_id || ""}
                    onChange={(e) => update(idx, { teacher_employee_id: e.target.value })}
                    className="w-full h-10 rounded-xl border border-input bg-white px-2 text-sm mt-1"
                    data-testid={`ind-slot-teacher-${idx}`}
                  >
                    <option value="">No teacher</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}{emp.position ? ` — ${emp.position}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Remove */}
                <Button
                  type="button" variant="ghost" size="icon"
                  onClick={() => remove(idx)}
                  className="h-10 w-10 rounded-xl text-brand-inkMuted hover:text-[#C62828]"
                  data-testid={`ind-slot-remove-${idx}`}
                  title="Remove slot"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!canAddDay && (
        <p className="text-[11px] text-brand-inkMuted mt-1.5">All 7 weekdays are used. You can still add specific dates.</p>
      )}
    </div>
  );
}
